/**
 * Enhanced Error Boundary with retry logic and fallback UI
 */

import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you might want to log to an error reporting service
    // this.logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { fallback: FallbackComponent, maxRetries = 3 } = this.props;

    if (hasError) {
      // Use custom fallback component if provided
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={error}
            retry={this.handleRetry}
            retryCount={retryCount}
            maxRetries={maxRetries}
          />
        );
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Something went wrong
            </h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md">
              {error?.message || 'An unexpected error occurred while loading this section.'}
            </p>
            
            {retryCount < maxRetries && (
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Try Again {retryCount > 0 && `(${retryCount}/${maxRetries})`}
              </button>
            )}
            
            {retryCount >= maxRetries && (
              <div className="text-sm text-gray-500">
                Maximum retry attempts reached. Please refresh the page or contact support.
              </div>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 text-left max-w-2xl mx-auto">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  Show error details (dev only)
                </summary>
                <pre className="mt-2 p-4 bg-red-50 border border-red-200 rounded text-xs overflow-auto">
                  {error && error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// API Error Component
export const ApiErrorComponent = ({ 
  error, 
  retry, 
  retryCount = 0, 
  maxRetries = 3,
  showDetails = false 
}) => {
  const getErrorMessage = (error) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error?.response?.status) {
      const statusMessages = {
        400: 'Invalid request. Please check your input and try again.',
        401: 'You are not authorized. Please log in again.',
        403: 'You do not have permission to perform this action.',
        404: 'The requested resource was not found.',
        500: 'Server error. Please try again later.',
        502: 'Service temporarily unavailable. Please try again.',
        503: 'Service unavailable. Please try again later.',
      };
      
      return statusMessages[error.response.status] || 'An error occurred while connecting to the server.';
    }
    
    return error?.message || 'An unexpected error occurred.';
  };

  const isRetryable = (error) => {
    // Retry on network errors or 5xx server errors
    return !error?.response || error.response.status >= 500;
  };

  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">
            Error Loading Data
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{getErrorMessage(error)}</p>
            
            {error?.response?.data?.fallback && (
              <p className="mt-1 text-red-600">
                ⚠️ Fallback data may be displayed if available.
              </p>
            )}
          </div>
          
          <div className="mt-4 flex space-x-3">
            {retry && isRetryable(error) && retryCount < maxRetries && (
              <button
                type="button"
                onClick={retry}
                className="inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <ArrowPathIcon className="h-3 w-3 mr-1" />
                Retry {retryCount > 0 && `(${retryCount}/${maxRetries})`}
              </button>
            )}
            
            {showDetails && (
              <button
                type="button"
                onClick={() => console.log('Full error:', error)}
                className="text-xs text-red-600 hover:text-red-500"
              >
                Show details in console
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Network Status Component
export const NetworkStatus = () => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div className="bg-red-600">
      <div className="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="w-0 flex-1 flex items-center">
            <span className="flex p-2 rounded-lg bg-red-800">
              <ExclamationTriangleIcon className="h-6 w-6 text-white" />
            </span>
            <p className="ml-3 font-medium text-white truncate">
              <span className="md:hidden">You are offline</span>
              <span className="hidden md:inline">
                No internet connection. Some features may be limited.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;